import { Moment } from 'moment-timezone';
import moment from 'moment-timezone';
import MasterData from '@spike/masterdata-model';
import { Option, PetType } from '@spike/model';
import { createEmptyPet, Pet } from '@spike/pet-model';
import { VaccineApproval, VaccineApprovalPet, VaccineApprovalVaccineItem } from 'model/VaccineApproval';

export const formatReceivedAt = (date: Moment | string | undefined, timeZone: string): string => {
    if (!date) return '';

    const value = moment.isMoment(date) ? date : moment(date);
    return value.tz(timeZone).format('MMM DD, YYYY - hh:mm a');
};

export const formatExpirationDate = (date: Moment | string | undefined, timeZone: string): string => {
    if (!date) return '';

    const value = moment.isMoment(date) ? date : moment(date);
    return value.tz(timeZone).format('MMM DD, YYYY');
};

export const getClientDisplayName = (firstName?: string, lastName?: string): string => {
    return [firstName, lastName].filter(Boolean).join(' ').trim();
};

export const getPrimaryVaccine = (item: VaccineApproval): VaccineApprovalVaccineItem | undefined => {
    if (item.vaccines?.length) {
        return item.vaccines.find(vaccine => String(vaccine.id) === String(item.vaccineId)) || item.vaccines[0];
    }
    return undefined;
};

export const getVaccineDisplayName = (item: VaccineApproval): string => {
    return getPrimaryVaccine(item)?.name || item.vaccineId || '—';
};

export const getVaccineExpiration = (item: VaccineApproval): Moment | undefined => {
    return getPrimaryVaccine(item)?.expirationDate || item.expirationDate;
};

export const getVaccineFileUrl = (item: VaccineApproval): string | undefined => {
    return item.file?.url;
};

export const getVaccineFileName = (item: VaccineApproval): string | undefined => {
    return item.file?.fileName;
};

const resolvePetType = (species: string | undefined, masterData: MasterData): PetType | undefined => {
    const normalized = (species || '').trim().toLowerCase();
    if (!normalized) {
        return masterData.petTypes.find(type => type.id === 'dogs') || masterData.petTypes[0];
    }

    if (normalized.includes('dog')) {
        return masterData.petTypes.find(type => type.id === 'dogs');
    }
    if (normalized.includes('cat')) {
        return masterData.petTypes.find(type => type.id === 'cats');
    }
    if (normalized.includes('exotic')) {
        return masterData.petTypes.find(type => type.id === 'exotics');
    }

    return (
        masterData.petTypes.find(
            type =>
                type.id === normalized ||
                type.singular?.toLowerCase() === normalized ||
                type.plural?.toLowerCase() === normalized
        ) || masterData.petTypes[0]
    );
};

const resolveBreed = (
    breedId: number | undefined,
    petType: PetType | undefined,
    masterData: MasterData
): Option<number> | undefined => {
    if (breedId === undefined || breedId === null) {
        return undefined;
    }

    const breedsForType =
        petType?.id === 'cats'
            ? masterData.breeds.cats
            : petType?.id === 'exotics'
            ? masterData.breeds.exotics
            : masterData.breeds.dogs;

    return (
        breedsForType.find(breed => breed.id === breedId) ||
        masterData.breeds.dogs.find(breed => breed.id === breedId) ||
        masterData.breeds.cats.find(breed => breed.id === breedId) ||
        masterData.breeds.exotics.find(breed => breed.id === breedId) || {
            id: breedId,
            name: String(breedId)
        }
    );
};

const formatWeightLabel = (weight?: string): string | undefined => {
    const value = weight?.trim();
    if (!value) {
        return undefined;
    }
    if (/lb|lbs|kg/i.test(value)) {
        return value;
    }
    return `${value} lb`;
};

export const mapVaccineApprovalPetToPet = (pet: VaccineApprovalPet, masterData: MasterData): Pet => {
    const type = resolvePetType(pet.species, masterData);
    const breed = resolveBreed(pet.breed, type, masterData);
    const weightLabel = formatWeightLabel(pet.weight);
    const weightNumber = pet.weight ? Number.parseFloat(pet.weight) : undefined;
    const base = createEmptyPet(() => String(pet.id), 0);

    return {
        ...base,
        id: pet.id,
        name: pet.name,
        type,
        breed,
        size: weightLabel ? { id: weightLabel, name: weightLabel } : undefined,
        weight: Number.isFinite(weightNumber) ? weightNumber : undefined,
        image: pet.profilePhotoUrl || ''
    };
};
